import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import useAutomationId from '@/hooks/useAutomationId.ts';
import { AutomationDiscardLatestDraftResponse } from '@/types/automations.ts';
import {
  AUTOMATION_KEY,
  AUTOMATION_LATEST_VERSION_KEY,
  AUTOMATION_VERSIONS_KEY,
} from '@/utils/queryKeys.ts';
import { get_automation_discard_latest_draft_url } from '@/utils/urls.ts';

type DiscardLatestDraftParams = {
  draftVersionId: string;
  expectedPublishedVersionId: string;
};

const useDiscardLatestDraft = () => {
  const apiClient = useApiClient();
  const automationId = useAutomationId();
  const queryClient = useQueryClient();

  return useMutation<
    AutomationDiscardLatestDraftResponse,
    AxiosError<{ detail?: string }>,
    DiscardLatestDraftParams
  >({
    retry: false,
    mutationFn: async ({ draftVersionId, expectedPublishedVersionId }) =>
      (
        await apiClient.post(get_automation_discard_latest_draft_url(automationId), {
          draft_version_id: draftVersionId,
          expected_published_version_id: expectedPublishedVersionId,
          confirmed: true,
        })
      ).data,
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: AUTOMATION_KEY(automationId) }),
        queryClient.invalidateQueries({ queryKey: AUTOMATION_LATEST_VERSION_KEY(automationId) }),
        queryClient.invalidateQueries({ queryKey: AUTOMATION_VERSIONS_KEY(automationId) }),
      ]);
    },
  });
};

export default useDiscardLatestDraft;
